import { useEffect, useImperativeHandle, useState } from 'react';

import { ajvResolver } from '@hookform/resolvers/ajv';
import { Divider, Fade, Stack, Typography, useTheme } from '@mui/material';
import { Box } from '@mui/system';
import { JSONSchemaType } from 'ajv';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import useSWR from 'swr';

import { ActionRelaysDropdown, DevicesAutocomplete, EmptyState, GeneralLoader } from '~components';
import LastOnline from '~components/data/lastOnline';
import RasterImage from '~components/media/rasterImage';
import { CommandTypeEnum, getCommandTypeDisplayName } from '~enums';
import DevicesService from '~features/devices/services/devicesService';
import { useFormContainer } from '~hooks';
import { BaseReference } from '~interfaces';
import Device from '~interfaces/device';
import { getService } from '~services/serviceFactory';
import { Dimens } from '~themes';

import schema from './linkControlBoxSchema.json';

const deviceService = getService(DevicesService);

export interface LinkControlBoxFormValues {
	controlBox: Device;
	action1: CommandTypeEnum | undefined;
	action2: CommandTypeEnum | undefined;
}

interface LinkControlBoxFormFields {
	controlBox: Device;
	action1: string;
	action2: string;
}

interface LinkControlBoxFormProps {
	organisationId: string | undefined;
	initialControlBoxId?: string | undefined;
	initialAction1?: string | undefined;
	initialAction2?: string | undefined;
	onSubmit: (value: LinkControlBoxFormValues) => void;
	onCancel: () => void;
}

const LinkControlBoxForm = ({
	organisationId,
	onSubmit,
	onCancel,
	...props
}: LinkControlBoxFormProps) => {
	const { t } = useTranslation();
	const { palette } = useTheme();
	const { setDisabled, formRef } = useFormContainer();
	const imageSize = '120px';
	// Load device
	const {
		data: deviceData,
		error: _deviceError,
		isLoading: deviceIsLoading,
	} = useSWR(
		deviceService.getDeviceByIdCacheKey(props.initialControlBoxId, organisationId),
		([_path, id, orgId]) => {
			if (id == undefined) return;
			return deviceService.getDeviceById(id, orgId ?? undefined);
		},
		{
			onSuccess(data, _key, _config) {
				setSelectedControlBox(data ?? null);
			},
			revalidateOnMount: true,
		}
	);

	const [selectedControlBox, setSelectedControlBox] = useState<Device | null>(deviceData ?? null);
	const [action1, setAction1] = useState<string | undefined>(props.initialAction1);
	const [action2, setAction2] = useState<string | undefined>(props.initialAction2);

	// Form control
	const { control, formState } = useForm<LinkControlBoxFormFields>({
		mode: 'onChange',
		resolver: ajvResolver(schema as unknown as JSONSchemaType<LinkControlBoxFormFields>),
		defaultValues: {
			action1: props.initialAction1,
			action2: props.initialAction2,
		},
	});

	useImperativeHandle(
		formRef,
		() => ({
			onSubmit: () => {
				if (!selectedControlBox) return;
				onSubmit?.({
					controlBox: selectedControlBox,
					action1: action1 ? (action1 as CommandTypeEnum) : undefined,
					action2: action2 ? (action2 as CommandTypeEnum) : undefined,
				});
			},
			onCancel: () => onCancel?.(),
		}),
		[onSubmit, selectedControlBox, action1, action2, onCancel]
	);

	useEffect(() => {
		let disable = !formState.isValid;
		if (selectedControlBox == null) {
			disable = true;
		}
		if (!action1 && !action2) {
			disable = true;
		}
		setDisabled?.(disable);
	}, [formState, selectedControlBox, setDisabled, action1, action2]);

	if (deviceIsLoading) return <GeneralLoader />;
	return (
		<Stack spacing={Dimens.spacing3xl}>
			<Controller
				name='controlBox'
				control={control}
				render={({ field }) => (
					<DevicesAutocomplete
						{...field}
						label={t('device.labels.single', { ns: 'entities' })}
						organisationId={organisationId}
						deviceModelName='SKBR02_CTRL' // FIXME: Think of a more consistent way for this. Hardcoded doesn't look clean, but device model isn't an enum, it's dynamic.
						onOptionSelected={(value: Device | undefined) => {
							setSelectedControlBox(value ?? null);
						}}
						value={selectedControlBox ?? null}
					/>
				)}
			/>
			<Box sx={{ position: 'relative' }}>
				<Fade in={selectedControlBox == undefined} appear={false} mountOnEnter unmountOnExit>
					<Stack sx={{ position: 'absolute', width: '100%' }} spacing={Dimens.spacing3xl}>
						<Divider />
						<EmptyState
							icon='icon/device'
							title={t('device.labels.linkControlBoxPleaseSelect', { ns: 'entities' })}
						/>
					</Stack>
				</Fade>
				<Fade in={selectedControlBox != undefined} mountOnEnter unmountOnExit>
					<Stack sx={{ position: 'absolute', width: '100%' }} alignItems='center'>
						<RasterImage name='device/model/skbr-ctrl' size={imageSize} />
						<Typography
							variant='label'
							textAlign='center'
							color={palette.textPrimary.main}
							sx={{ mb: Dimens.spacingXs }}
						>
							{selectedControlBox?.name} ({selectedControlBox?.hardwareId})
						</Typography>
						<LastOnline date={selectedControlBox?.state?.lastReport} />
						<Controller
							name='action1'
							control={control}
							render={({ field: _field }) => (
								<ActionRelaysDropdown
									selectedValue={
										action1 ?
											({
												id: action1,
												name: getCommandTypeDisplayName(action1 as CommandTypeEnum, t),
											} as BaseReference)
										:	undefined
									}
									sx={{ width: '100%', mt: Dimens.spacing3xl }}
									relayNumber={1}
									label={t('actionRelay.labels.single', { ns: 'entities' }) + ' 1'}
									onValueChanged={(newValue?: BaseReference) => {
										setAction1(newValue?.id);
									}}
								/>
							)}
						/>
						<Controller
							name='action2'
							control={control}
							render={({ field: _field }) => (
								<ActionRelaysDropdown
									selectedValue={
										action2 ?
											({
												id: action2,
												name: getCommandTypeDisplayName(action2 as CommandTypeEnum, t),
											} as BaseReference)
										:	undefined
									}
									sx={{ width: '100%', mt: Dimens.spacingXl }}
									relayNumber={2}
									label={t('actionRelay.labels.single', { ns: 'entities' }) + ' 2'}
									onValueChanged={(newValue?: BaseReference) => {
										setAction2(newValue?.id);
									}}
								/>
							)}
						/>
					</Stack>
				</Fade>
			</Box>
		</Stack>
	);
};

export default LinkControlBoxForm;
